import filter from 'lodash/filter';
import get from 'lodash/get';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import md5 from 'md5';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { APIConfiguration } from '@savgroup-front-common/configuration';
import { logCritical } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { SUPPORTED_METHODS } from '@savgroup-front-common/constants/shared';
import { selectLocale } from '@savgroup-front-common/core/domains/i18n/selectors';
import {
  arrayBufferFromBlob,
  getFileType,
  throttledTakeLatest,
} from '@savgroup-front-common/core/helpers';
import {
  executeSagaRequest,
  reduxExtendedRequestSaga,
} from '@savgroup-front-common/core/services';

import { getClaimsByOrderIdRequest } from '../../api/Claim/getClaimsByOrderId';
import { closeTicketRequest } from '../../api/Marketplace/closeTicketCommand';
import { getSellerMarketplaceCapabilitiesRequest } from '../../api/Marketplace/getSellerMarketplaceCapabilitiesQuery';
import { listOrderTicketsRequest } from '../../api/Marketplace/listOrderTicketsQuery';
import { listTicketsRequest } from '../../api/Marketplace/listTicketsQuery';
import { refreshTicketRequest } from '../../api/Marketplace/refreshTicketCommand';
import { snoozeTicketRequest } from '../../api/Marketplace/snoozeTicketCommand';
import { trackTicketActivityRequest } from '../../api/Marketplace/trackTicketActivityCommand';
import { createOwnerForOrderRequest } from '../../api/Owner/createOwnerForOrderCommand';
import { getListOwnerProductsRequest } from '../../api/Owner/getListOwnerProductsQuery';
import { getFilesByOrderIdRequest } from '../../api/Workflow/files/getFilesByOrderIdQuery';

import {
  CLOSE_MARKETPLACE_TICKET,
  CREATE_OWNER_FOR_ORDER,
  LOAD_CLAIM_INFO_BY_ORDER,
  LOAD_CLAIMS_BY_ORDER,
  LOAD_FILES_BY_ORDER,
  LOAD_MARKETPLACE_CAPABILITIES_BY_SELLER,
  LOAD_MARKETPLACE_TICKETS,
  LOAD_MARKETPLACE_TICKETS_BY_ORDER,
  LOAD_OWNER_PRODUCTS,
  REFRESH_TICKET_DETAILS,
  SNOOZE_TICKET,
  TRACK_USER_ACTIVITY_ON_TICKET,
  UPLOAD_MARKETPLACE_ATTACHMENTS,
} from './actionTypes';

function* loadMarketplaceTicketsWorker({ payload }) {
  const { filters, searchOrder, searchQuery } = payload || {};
  const formattedFilters = {
    ...mapValues(filters, (toggles) =>
      filter(map(toggles, (isToggled, name) => (isToggled ? name : undefined))),
    ),
    ...searchOrder,
  };

  if (searchQuery) {
    formattedFilters.orderReference = searchQuery;
  }

  yield executeSagaRequest(
    LOAD_MARKETPLACE_TICKETS,
    listTicketsRequest(formattedFilters),
  );
  yield put(LOAD_MARKETPLACE_TICKETS.end());
}

function* refreshTicketDetailsWorker({ payload: ticketId }) {
  yield executeSagaRequest(
    REFRESH_TICKET_DETAILS,
    refreshTicketRequest({ ticketId }),
    {
      ticketId,
    },
  );
  yield put(REFRESH_TICKET_DETAILS.end());
}

function* closeMarketplaceTicketWorker({ payload }) {
  const { ticketId, refreshList } = payload;

  yield executeSagaRequest(
    CLOSE_MARKETPLACE_TICKET,
    closeTicketRequest({ ticketId }),
    {
      ticketId,
    },
  );
  yield put(CLOSE_MARKETPLACE_TICKET.end());
  if (refreshList) {
    refreshList();
  }
}

function* loadClaimInfoByOrderWorker({ payload: { orderId } }) {
  yield put(LOAD_CLAIM_INFO_BY_ORDER.start({ orderId, indexer: orderId }));
  const claimsResponseAction = yield executeSagaRequest(
    LOAD_CLAIMS_BY_ORDER,
    getClaimsByOrderIdRequest({ orderId }),
    { indexer: orderId },
  );

  const filesResponseAction = yield executeSagaRequest(
    LOAD_FILES_BY_ORDER,
    getFilesByOrderIdRequest({ orderId }),
    { indexer: orderId },
  );

  if (
    claimsResponseAction.type === LOAD_CLAIMS_BY_ORDER.SUCCEEDED &&
    (filesResponseAction === null ||
      get(filesResponseAction, 'type') === LOAD_FILES_BY_ORDER.SUCCEEDED)
  ) {
    yield put(
      LOAD_CLAIM_INFO_BY_ORDER.success(
        {
          claimsInfo: get(claimsResponseAction.payload, 'value'),
          filesInfo: get(filesResponseAction, ['payload', 'value']),
        },
        { orderId, indexer: orderId },
      ),
    );
  } else if (
    claimsResponseAction.type === LOAD_CLAIMS_BY_ORDER.CANCELLED ||
    get(filesResponseAction, 'type') === LOAD_FILES_BY_ORDER.CANCELLED
  ) {
    yield put(LOAD_CLAIM_INFO_BY_ORDER.cancel({ orderId, indexer: orderId }));
  } else {
    yield put(
      LOAD_CLAIM_INFO_BY_ORDER.error(null, { orderId, indexer: orderId }),
    );
  }
  yield put(LOAD_CLAIM_INFO_BY_ORDER.end({ indexer: orderId }));
}
function* loadClaimInfoByOrderWatcher() {
  yield takeEvery(LOAD_CLAIM_INFO_BY_ORDER.BASE, loadClaimInfoByOrderWorker);
}

function* createOwnerForOrderWorker({ payload: orderId }) {
  const meta = {
    orderId,
  };

  yield executeSagaRequest(
    CREATE_OWNER_FOR_ORDER,
    createOwnerForOrderRequest({ orderId }),
    meta,
  );
  yield put(CREATE_OWNER_FOR_ORDER.end(meta));
}

function* loadMarketplaceTicketsByOrderWorker({ payload: orderId }) {
  yield executeSagaRequest(
    LOAD_MARKETPLACE_TICKETS_BY_ORDER,
    listOrderTicketsRequest({ orderId }),
    {
      orderId,
    },
  );
  yield put(LOAD_MARKETPLACE_TICKETS_BY_ORDER.end());
}

const ApiConnector = {
  BACKMARKET: 'BackMarket',
  CDISCOUNT: 'CDiscount',
  PRICE_MINISTER: 'PriceMinister',
  FNAC: 'Fnac',
  AMZON: 'Amazon',
};

function* loadMarketplaceCapabilitiesBySellerWorker({
  payload: { marketplace, sellerId },
}) {
  if (!sellerId) {
    yield put(LOAD_MARKETPLACE_CAPABILITIES_BY_SELLER.end());

    return;
  }

  if (!Object.values(ApiConnector).includes(marketplace)) {
    return;
  }

  yield executeSagaRequest(
    LOAD_MARKETPLACE_CAPABILITIES_BY_SELLER,
    getSellerMarketplaceCapabilitiesRequest({ sellerId, marketplace }),
    {
      marketplace,
      sellerId,
    },
  );
  yield put(LOAD_MARKETPLACE_CAPABILITIES_BY_SELLER.end());
}

function* snoozeTicketWorker({ payload }) {
  const { id, date, refreshList } = payload;
  const meta = { id };

  yield executeSagaRequest(
    SNOOZE_TICKET,
    snoozeTicketRequest({ id, date }),
    meta,
  );
  yield put(SNOOZE_TICKET.end(meta));
  if (refreshList) {
    refreshList();
  }
}

function* uploadMarketplaceAttachmentsWorker({ payload: { ticketId, file } }) {
  const language = yield select(selectLocale);
  const fileArrayBuffer = yield call(arrayBufferFromBlob, file);
  const hash = md5(fileArrayBuffer);
  const fileType = getFileType(new Uint8Array(fileArrayBuffer));

  if (fileType && fileType.mime !== file.type) {
    // eslint-disable-next-line no-console
    console.warn(
      // eslint-disable-next-line max-len
      `[uploadMarketplaceAttachmentsWorker] file '${file.name}' uploaded had an incorrect mime type. Before: ${file.type}, After: ${fileType.mime}`,
    );
  }

  const fileToUpload = fileType
    ? new File(
        [fileArrayBuffer],
        fileType.ext
          ? file.name.replace(/(\.[^/.]*)?$/, `.${fileType.ext}`)
          : file.name,
        {
          type: fileType.mime || file.type,
        },
      )
    : file;

  const body = new FormData();

  body.append('file', fileToUpload);
  const meta = {
    ticketId,
    hash,
    fileName: fileToUpload.name,
    mimeType: fileToUpload.type,
    fileSize: fileToUpload.size,
  };

  yield call(
    reduxExtendedRequestSaga,
    UPLOAD_MARKETPLACE_ATTACHMENTS,
    `${APIConfiguration.marketplace}tickets/${ticketId}/attachments`,
    {
      method: SUPPORTED_METHODS.POST,
      body,
      language,
    },
    meta,
  );
  yield put(UPLOAD_MARKETPLACE_ATTACHMENTS.end(meta));
}

function* trackUserActivityOnTicketWorker({ payload }) {
  const { ticketId, userId } = payload;
  const meta = {
    ticketId,
    userId,
  };

  yield executeSagaRequest(
    TRACK_USER_ACTIVITY_ON_TICKET,
    trackTicketActivityRequest({ ticketId, userId }),
    meta,
  );

  yield put(TRACK_USER_ACTIVITY_ON_TICKET.end(meta));
}

function* loadMarketplaceTicketsWatcher() {
  yield throttledTakeLatest(
    LOAD_MARKETPLACE_TICKETS.BASE,
    loadMarketplaceTicketsWorker,
  );
}

function* closeMarketplaceTicketWatcher() {
  yield takeEvery(CLOSE_MARKETPLACE_TICKET.BASE, closeMarketplaceTicketWorker);
}

function* refreshTicketDetailsWatcher() {
  yield throttledTakeLatest(
    REFRESH_TICKET_DETAILS.BASE,
    refreshTicketDetailsWorker,
  );
}

function* createOwnerForOrderWatcher() {
  yield takeEvery(CREATE_OWNER_FOR_ORDER.BASE, createOwnerForOrderWorker);
}

function* loadMarketplaceTicketsByOrderWatcher() {
  yield takeEvery(
    LOAD_MARKETPLACE_TICKETS_BY_ORDER.BASE,
    loadMarketplaceTicketsByOrderWorker,
  );
}

function* loadMarketplaceCapabilitiesBySellerWatcher() {
  yield takeEvery(
    LOAD_MARKETPLACE_CAPABILITIES_BY_SELLER.BASE,
    loadMarketplaceCapabilitiesBySellerWorker,
  );
}

function* snoozeTicketWatcher() {
  yield takeEvery(SNOOZE_TICKET.BASE, snoozeTicketWorker);
}

function* uploadMarketplaceAttachmentsWatcher() {
  yield takeEvery(
    UPLOAD_MARKETPLACE_ATTACHMENTS.BASE,
    uploadMarketplaceAttachmentsWorker,
  );
}

function* trackUserActivityOnTicketWatcher() {
  yield takeEvery(
    TRACK_USER_ACTIVITY_ON_TICKET.BASE,
    trackUserActivityOnTicketWorker,
  );
}

function* loadOwnerProductsWorker({ payload }) {
  yield executeSagaRequest(
    LOAD_OWNER_PRODUCTS,
    getListOwnerProductsRequest({ ownerId: payload }),
    payload,
  );
  yield put(LOAD_OWNER_PRODUCTS.end());
}

function* loadOwnerProductsWatcher() {
  yield takeEvery(LOAD_OWNER_PRODUCTS.BASE, loadOwnerProductsWorker);
}

export default function* mainSaga() {
  try {
    yield all([
      loadMarketplaceTicketsWatcher(),
      closeMarketplaceTicketWatcher(),
      loadClaimInfoByOrderWatcher(),
      createOwnerForOrderWatcher(),
      loadMarketplaceTicketsByOrderWatcher(),
      loadMarketplaceCapabilitiesBySellerWatcher(),
      snoozeTicketWatcher(),
      uploadMarketplaceAttachmentsWatcher(),
      trackUserActivityOnTicketWatcher(),
      refreshTicketDetailsWatcher(),
      loadOwnerProductsWatcher(),
    ]);
  } catch (error) {
    logCritical(error);
  }
}
